// src\components\Portfolio\PortfolioSkeleton.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

// Animation variants for staggered items
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

function PortfolioSkeleton() {
  // Generate random aspect ratios for realistic masonry layout
  const generateRandomRatios = count => {
    const ratios = [];
    for (let i = 0; i < count; i++) {
      const random = Math.random();
      if (random < 0.3) {
        ratios.push({ ratio: 1.5, type: 'landscape' });
      } else if (random < 0.5) {
        ratios.push({ ratio: 0.7, type: 'portrait' });
      } else {
        ratios.push({ ratio: 1, type: 'square' });
      }
    }
    return ratios;
  };

  const skeletonItems = generateRandomRatios(12);

  // Inject CSS animations into document head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }

      @keyframes pulse-subtle {
        0%, 100% {
          opacity: 0.4;
        }
        50% {
          opacity: 0.8;
        }
      }

      @keyframes bounce-skeleton {
        0%, 60%, 100% {
          transform: translateY(0);
        }
        30% {
          transform: translateY(-10px);
        }
      }

      @keyframes gradient-shift {
        0% {
          background-position: 0% 50%;
        }
        50% {
          background-position: 100% 50%;
        }
        100% {
          background-position: 0% 50%;
        }
      }

      @keyframes scale-pulse {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(0.95);
        }
      }

      .animate-shimmer {
        animation: shimmer 1.8s infinite;
      }

      .animate-pulse-subtle {
        animation: pulse-subtle 1.5s ease-in-out infinite;
      }

      .animate-bounce-skeleton {
        animation: bounce-skeleton 1.4s infinite ease-in-out;
      }

      .animate-gradient-shift {
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .animate-scale-pulse {
        animation: scale-pulse 2s ease-in-out infinite;
      }

      .skeleton-base {
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
        border-radius: 8px;
      }

      .skeleton-card {
        background: var(--bg);
        border: 1px solid var(--border);
        border-radius: 12px;
        overflow: hidden;
        position: relative;
      }

      .skeleton-card .shimmer-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.05) 30%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.05) 70%,
          transparent 100%
        );
        animation: shimmer 2s infinite;
        pointer-events: none;
      }

      .dark .skeleton-card .shimmer-overlay {
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.03) 30%,
          rgba(255, 255, 255, 0.05) 50%,
          rgba(255, 255, 255, 0.03) 70%,
          transparent 100%
        );
      }

      .skeleton-image {
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
        position: relative;
        overflow: hidden;
      }

      .skeleton-image .shimmer-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.06) 30%,
          rgba(255, 255, 255, 0.1) 50%,
          rgba(255, 255, 255, 0.06) 70%,
          transparent 100%
        );
        animation: shimmer 2s infinite;
        pointer-events: none;
      }

      .dark .skeleton-image .shimmer-overlay {
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.03) 30%,
          rgba(255, 255, 255, 0.05) 50%,
          rgba(255, 255, 255, 0.03) 70%,
          transparent 100%
        );
      }

      .skeleton-text {
        height: 12px;
        border-radius: 4px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-text-sm {
        height: 10px;
        border-radius: 3px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-text-lg {
        height: 20px;
        border-radius: 6px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-circle {
        border-radius: 50%;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-badge {
        border-radius: 9999px;
        background: linear-gradient(
          135deg,
          rgba(0,0,0,0.3) 0%,
          rgba(0,0,0,0.5) 50%,
          rgba(0,0,0,0.3) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-pill {
        border-radius: 9999px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-filter {
        border-radius: 9999px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
        height: 36px;
      }

      .skeleton-filter-sm {
        border-radius: 9999px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
        height: 28px;
      }

      /* Filter button shimmer */
      .skeleton-filter::after,
      .skeleton-filter-sm::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.05) 30%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.05) 70%,
          transparent 100%
        );
        animation: shimmer 2s infinite;
        pointer-events: none;
        border-radius: 9999px;
      }

      .dark .skeleton-filter::after,
      .dark .skeleton-filter-sm::after {
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.03) 30%,
          rgba(255, 255, 255, 0.05) 50%,
          rgba(255, 255, 255, 0.03) 70%,
          transparent 100%
        );
      }

      .skeleton-filter,
      .skeleton-filter-sm {
        position: relative;
        overflow: hidden;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-8"
    >
      {/* Loading Header */}
      <motion.div variants={itemVariants} className="text-center mb-12">
        <div
          className="inline-block w-32 h-6 rounded-full mb-4 skeleton-pill"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div
          className="w-64 h-10 rounded-lg mx-auto mb-3 skeleton-text-lg"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div
          className="w-96 h-4 rounded-lg mx-auto skeleton-text"
          style={{ backgroundColor: 'var(--border)' }}
        />
      </motion.div>

      {/* Stats Section Skeleton */}
      <motion.div
        variants={containerVariants}
        className="py-8 px-4 border-t border-b"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-3 gap-8 text-center">
            {[...Array(3)].map((_, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="space-y-2"
              >
                <div className="skeleton-text-lg w-20 h-8 mx-auto" />
                <div className="skeleton-text-sm w-24 h-4 mx-auto" />
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Filter Buttons Skeleton */}
      <motion.div
        variants={itemVariants}
        className="flex flex-wrap justify-center gap-2 md:gap-4 mb-3"
      >
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            className="skeleton-filter"
            style={{
              width:
                i === 1
                  ? '120px'
                  : i === 2
                    ? '140px'
                    : i === 3
                      ? '130px'
                      : '110px',
            }}
          />
        ))}
      </motion.div>

      {/* Type Filters Skeleton */}
      <motion.div
        variants={itemVariants}
        className="flex flex-wrap justify-center gap-2 mb-8"
      >
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className="skeleton-filter-sm"
            style={{
              width: i === 1 ? '100px' : i === 2 ? '90px' : '110px',
            }}
          />
        ))}
      </motion.div>

      {/* Masonry Grid Skeleton */}
      <motion.div
        variants={containerVariants}
        className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6"
      >
        {skeletonItems.map((item, idx) => {
          const paddingBottom = `${(1 / item.ratio) * 100}%`;

          return (
            <motion.div
              key={idx}
              variants={itemVariants}
              className="break-inside-avoid group relative rounded-2xl overflow-hidden skeleton-card"
            >
              {/* Main Image Skeleton */}
              <div
                className="relative w-full overflow-hidden skeleton-image"
                style={{
                  paddingBottom: paddingBottom,
                }}
              >
                <div className="shimmer-overlay" />

                {/* Content Placeholder with Pulse Animation */}
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="transform transition-all duration-500 translate-y-4 group-hover:translate-y-0 w-full text-center">
                    {/* Icon */}
                    <div
                      className="w-12 h-12 rounded-full mx-auto mb-4 skeleton-circle animate-scale-pulse"
                      style={{ backgroundColor: 'var(--bg)' }}
                    />

                    {/* Title */}
                    <div
                      className="w-3/4 h-5 rounded mx-auto mb-2 skeleton-text-lg"
                      style={{ backgroundColor: 'var(--bg)' }}
                    />

                    {/* Description line 1 */}
                    <div
                      className="w-1/2 h-3 rounded mx-auto mb-1 skeleton-text"
                      style={{ backgroundColor: 'var(--bg)' }}
                    />

                    {/* Description line 2 */}
                    <div
                      className="w-2/3 h-3 rounded mx-auto skeleton-text"
                      style={{ backgroundColor: 'var(--bg)' }}
                    />

                    {/* Button */}
                    <div
                      className="w-28 h-8 rounded-full mx-auto mt-4 skeleton-pill"
                      style={{ backgroundColor: 'var(--bg)' }}
                    />
                  </div>
                </div>
              </div>

              {/* Top Badge Skeletons */}
              <div className="absolute top-3 left-3 flex flex-col gap-1">
                <div
                  className="w-20 h-5 rounded-md skeleton-badge"
                  style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                />
                <div
                  className="w-16 h-5 rounded-md skeleton-badge"
                  style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                />
              </div>

              {/* Like Count Skeleton */}
              <div className="absolute bottom-3 right-3">
                <div
                  className="w-10 h-5 rounded-full skeleton-badge"
                  style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                />
              </div>

              {/* Image Count Skeleton */}
              <div className="absolute top-3 right-3">
                <div
                  className="w-10 h-5 rounded-md skeleton-badge"
                  style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                />
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Loading More Indicator */}
      <motion.div variants={itemVariants} className="text-center py-8">
        <div className="inline-flex items-center gap-3">
          <div
            className="w-2.5 h-2.5 rounded-full animate-bounce-skeleton"
            style={{
              backgroundColor: 'var(--text)',
              animationDelay: '0s',
            }}
          />
          <div
            className="w-2.5 h-2.5 rounded-full animate-bounce-skeleton"
            style={{
              backgroundColor: 'var(--text)',
              animationDelay: '0.2s',
            }}
          />
          <div
            className="w-2.5 h-2.5 rounded-full animate-bounce-skeleton"
            style={{
              backgroundColor: 'var(--text)',
              animationDelay: '0.4s',
            }}
          />
        </div>
      </motion.div>
    </motion.div>
  );
}

export default PortfolioSkeleton;
